import { rotatePoints } from "@/components/SlabLayoutBoard";
import { portalAsset } from "@/lib/portalApi";

const polyArea = (pts) => {
  let a = 0;
  for (let i = 0; i < (pts || []).length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
};

// read-only view of how the pieces sit on each slab, with the slab photo behind
export const PortalSlabLayout = ({ quote }) => {
  const layout = quote?.layout || {};
  const slabs = layout.slabs || [];
  const placements = layout.placements || {};
  const pieces = (quote?.areas || []).flatMap((a) =>
    (a.pieces || []).map((p) => ({ ...p, area_name: a.name || "Area" })),
  );

  if (!slabs.length)
    return (
      <p className="text-sm text-zinc-500" data-testid="portal-layout-empty">
        The slab layout for this quote has not been set yet.
      </p>
    );

  return (
    <div className="space-y-6" data-testid="portal-slab-layout">
      {slabs.map((s, i) => {
        const on = pieces.filter((p) => placements[p.id]?.slab === i);
        const used = on.reduce((n, p) => n + polyArea(p.points || []) / 144, 0);
        return (
          <div key={s.id || i} data-testid={`portal-slab-${i}`}>
            <div className="flex flex-wrap items-baseline gap-2 pb-1.5">
              <span className="text-sm font-semibold text-zinc-100">
                Slab {i + 1}
              </span>
              <span className="text-xs text-zinc-500">
                {s.material || quote.slab_color} {s.thickness}
              </span>
              <span className="font-mono-data ml-auto text-[11px] text-zinc-500">
                {s.length_in}" x {s.width_in}" · {used.toFixed(1)} ft² used
              </span>
            </div>
            <svg
              viewBox={`0 0 ${s.length_in} ${s.width_in}`}
              style={{ aspectRatio: `${s.length_in} / ${s.width_in}` }}
              className="w-full border border-zinc-700 bg-zinc-800"
            >
              <rect width={s.length_in} height={s.width_in} fill="#e7e5e4" />
              {s.photo && (
                <image
                  href={portalAsset(s.photo)}
                  width={s.length_in}
                  height={s.width_in}
                  preserveAspectRatio="xMidYMid slice"
                />
              )}
              {on.map((p) => {
                const pl = placements[p.id];
                const geo = rotatePoints(p.points, pl.rot || 0);
                return (
                  <g
                    key={p.id}
                    transform={`translate(${pl.x} ${pl.y})`}
                    data-testid={`portal-layout-piece-${p.id}`}
                  >
                    <polygon
                      points={geo.pts.map(([x, y]) => `${x},${y}`).join(" ")}
                      fill="rgba(29,78,216,0.20)"
                      stroke="#1d4ed8"
                      strokeWidth="0.6"
                    />
                    <text x="2" y="5.5" fill="#0f172a" fontSize="4">
                      {`${p.area_name} · ${p.label}`}
                    </text>
                  </g>
                );
              })}
              {(layout.cuts || [])
                .filter((c) => c.slab === i)
                .map((c) => (
                  <line
                    key={c.id}
                    x1={c.dir === "v" ? c.at : 0}
                    y1={c.dir === "v" ? 0 : c.at}
                    x2={c.dir === "v" ? c.at : s.length_in}
                    y2={c.dir === "v" ? s.width_in : c.at}
                    stroke="#b45309"
                    strokeWidth="0.5"
                    strokeDasharray="3 2"
                  />
                ))}
            </svg>
          </div>
        );
      })}
    </div>
  );
};
